<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<!-- SPEC 29 / P37-4 — Termo de Consentimento do Embaixador (ler → aceitar → feedback) -->
<script setup>
import { computed, ref } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  AMBASSADOR_TERM_ALIAS,
  AMBASSADOR_TERM_VERSION,
  buildConsentTerm,
} from './consentTermMessages';

const props = defineProps({
  consent: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  saving: { type: String, default: '' },
  companyName: { type: String, default: 'Imudei' },
  privacyUrl: { type: String, default: '' },
  // Funções do pai que retornam Promise; a máquina de estados é local.
  acceptFn: { type: Function, required: true },
  revokeFn: { type: Function, required: true },
});

const term = computed(() => buildConsentTerm(props.companyName));
const localView = ref(''); // '', 'success', 'error'

const acceptedVersion = computed(
  () => props.consent?.metadata?.accepted_term_version
);
const isEnrolled = computed(
  () =>
    props.consent?.status === 'accepted' &&
    acceptedVersion.value === AMBASSADOR_TERM_VERSION
);

const view = computed(() => {
  if (localView.value) return localView.value;
  return isEnrolled.value ? 'enrolled' : 'term';
});

const acceptedAtLabel = computed(() => props.consent?.accepted_at || '—');

const handleAccept = async () => {
  try {
    await props.acceptFn({
      accepted_term_version: AMBASSADOR_TERM_VERSION,
      accepted_term_alias: AMBASSADOR_TERM_ALIAS,
      accepted_text: 'Programa Embaixador — Comunidade de Associados',
      source: 'dashboard_relatorio_embaixador',
      category_alias: 'USER',
    });
    localView.value = 'success';
  } catch {
    localView.value = 'error';
  }
};

const handleRevoke = async () => {
  await props.revokeFn({ revoked_via: 'in_app' });
  localView.value = '';
};

const backToTerm = () => {
  localView.value = '';
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <LaisCard>
    <div v-if="loading" class="py-12 text-center text-lais-text-muted">
      Carregando termo...
    </div>

    <!-- TERM -->
    <div v-else-if="view === 'term'" class="flex flex-col gap-4">
      <h2 class="text-[18px] font-semibold text-lais-text">{{ term.title }}</h2>
      <section>
        <h3 class="text-[14px] font-semibold text-lais-text">
          {{ term.scopeTitle }}
        </h3>
        <p class="mt-1 text-[13px] leading-6 text-lais-text-body">
          {{ term.scopeDescription }}
        </p>
      </section>
      <section>
        <h3 class="text-[14px] font-semibold text-lais-text">
          {{ term.personalDataTitle }}
        </h3>
        <p class="mt-1 text-[13px] leading-6 text-lais-text-body">
          {{ term.personalDataDescription }}
        </p>
      </section>
      <hr class="border-lais-border" />
      <div class="flex max-h-[40vh] flex-col gap-3 overflow-auto pr-1">
        <p
          v-for="(paragraph, index) in term.paragraphs"
          :key="index"
          class="text-[13px] leading-6 text-lais-text-body"
        >
          {{ paragraph }}
        </p>
        <p class="text-[13px] leading-6 text-lais-text-body">
          {{ term.privacyParagraphPrefix }}
          <a
            v-if="privacyUrl"
            :href="privacyUrl"
            target="_blank"
            rel="license noopener noreferrer"
            class="text-lais-primary underline"
            >{{ term.privacyLinkLabel }}</a
          >
          <span v-else class="font-medium">{{ term.privacyLinkLabel }}</span
          >.
        </p>
      </div>
      <div class="sticky bottom-0 bg-white pt-2">
        <LaisPrimaryButton
          class="w-full sm:w-auto"
          icon="i-lucide-check"
          :is-loading="saving === 'accept'"
          @click="handleAccept"
        >
          {{ term.acceptButton }}
        </LaisPrimaryButton>
      </div>
    </div>

    <!-- SUCCESS / ENROLLED -->
    <div
      v-else-if="view === 'success' || view === 'enrolled'"
      class="flex flex-col items-start gap-3 py-4"
    >
      <span class="i-lucide-circle-check text-[28px] text-lais-teal" />
      <h2 class="text-[18px] font-semibold text-lais-text">
        {{ term.registrationConfirmedTitle }}
      </h2>
      <p class="text-[13px] leading-6 text-lais-text-body">
        {{ term.registrationConfirmedDescription }}
      </p>
      <p class="text-[12px] text-lais-text-muted">
        Versão: {{ acceptedVersion || AMBASSADOR_TERM_VERSION }} · Aceito em:
        {{ acceptedAtLabel }}
      </p>
      <button
        type="button"
        class="text-[13px] font-semibold text-lais-ruby underline"
        :disabled="saving === 'revoke'"
        @click="handleRevoke"
      >
        Revogar consentimento
      </button>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="view === 'error'"
      class="flex flex-col items-start gap-3 py-4"
    >
      <span class="i-lucide-circle-alert text-[28px] text-lais-ruby" />
      <h2 class="text-[18px] font-semibold text-lais-text">
        {{ term.errorTitle }}
      </h2>
      <p class="text-[13px] leading-6 text-lais-text-body">
        {{ term.errorDescription }}
      </p>
      <LaisPrimaryButton icon="i-lucide-rotate-ccw" @click="backToTerm">
        {{ term.tryAgain }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>
</template>
